import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { RoleIncentiveLedger } from './RoleIncentiveLedger';

const COLORS = { primary: '#192650', accent: '#0DA891' };

const formatDateIST = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

// ─── BONUS-only labels (base_commission removed — that's now the Flown Commissions tab) ──
const BONUS_TYPE_LABELS = {
  power_hours_bonus: 'Power Hours Bonus',
  weekly_bonus: 'Weekly Bonus',
  monthly_champion: 'Monthly Champion',
  first_strike_champion: 'First Strike',
  team_bonus: 'Team Bonus',
  intake_champion: 'Intake Champion',
};

const getBonusBadge = (type) => {
  const styles = {
    power_hours_bonus: 'bg-yellow-100 text-yellow-800',
    weekly_bonus: 'bg-green-100 text-green-800',
    monthly_champion: 'bg-amber-100 text-amber-800',
    first_strike_champion: 'bg-red-100 text-red-800',
    team_bonus: 'bg-purple-100 text-purple-800',
    intake_champion: 'bg-emerald-100 text-emerald-800',
  };
  return styles[type] || 'bg-gray-100 text-gray-800';
};

export default function CounselorIncentiveRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canMarkPaid = user?.role === 'admin' || user?.role === 'accountant';
  const canFilter = ['admin', 'operating_head'].includes(user?.role);

  // ─── filters (shared by both tabs) — all-time by default, no date pickers ──
  const [filters, setFilters] = useState({
    counselor_id: '',
    paid_status: '',
  });
  const [counselors, setCounselors] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');

  // ─── flown commissions state ──────────────────────────────────────────────
  const [flownLoading, setFlownLoading] = useState(true);
  const [flownData, setFlownData] = useState({ records: [], totals: {}, by_counselor: [] });

  // ─── bonuses (legacy endpoint, base_commission filtered OUT) ──────────────
  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonusRecords, setBonusRecords] = useState([]);
  const [bonusTotals, setBonusTotals] = useState({ total_amount: 0, total_paid: 0, total_pending: 0 });

  // ─── lifetime payout summary (paid only, all counselors incl. deactivated) ─
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({ rows: [], totals: { grand_total: 0, grand_commission: 0, grand_bonus: 0, counselor_count: 0 } });

  // ─── Mark Paid dialog ─────────────────────────────────────────────────────
  const [payDialog, setPayDialog] = useState({ open: false, row: null });
  const [paymentForm, setPaymentForm] = useState({ payment_method: 'bank_transfer', payment_reference: '', notes: '' });
  const [processing, setProcessing] = useState(false);

  // ─── Reverse Paid confirm ─────────────────────────────────────────────────
  const [reverseDialog, setReverseDialog] = useState({ open: false, row: null });

  useEffect(() => {
    if (canFilter) apiClient.get('/users/counsellors').then(r => setCounselors(r.data || [])).catch(() => {});
  }, [canFilter]);

  // ── Fetch flown commissions ─────────────────────────────────────────────
  const fetchFlown = useCallback(async () => {
    setFlownLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.counselor_id) params.append('counselor_id', filters.counselor_id);
      if (filters.paid_status) params.append('paid_status', filters.paid_status);
      const r = await apiClient.get(`/finance/flown-incentives?${params}`);
      setFlownData(r.data || { records: [], totals: {}, by_counselor: [] });
    } catch (err) {
      console.error('flown-incentives error', err);
      toast.error('Failed to load flown commissions');
    } finally {
      setFlownLoading(false);
    }
  }, [filters]);

  // ── Fetch bonuses (legacy) ─────────────────────────────────────────────
  const fetchBonuses = useCallback(async () => {
    setBonusLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.counselor_id) params.append('counselor_id', filters.counselor_id);
      if (filters.paid_status) params.append('payment_status', filters.paid_status);
      params.append('limit', '500');
      const r = await apiClient.get(`/counselor-incentive-records?${params}`);
      // Filter out base_commission — those live in the Flown tab now
      const filtered = (r.data?.records || []).filter(rec => rec.incentive_type !== 'base_commission');
      setBonusRecords(filtered);
      // Recompute totals locally to match the filtered set.
      // Rejected records must NOT count toward total or pending.
      const isPaid = (r) => r.payment_status === 'paid' || r.status === 'paid' || r.status === 'approved';
      const isRej = (r) => r.payment_status === 'rejected' || r.status === 'rejected';
      const ta = filtered.filter(r => !isRej(r)).reduce((s, r) => s + (r.amount || 0), 0);
      const tp = filtered.filter(isPaid).reduce((s, r) => s + (r.amount || 0), 0);
      setBonusTotals({ total_amount: ta, total_paid: tp, total_pending: Math.max(0, ta - tp) });
    } catch (err) {
      console.error('bonuses error', err);
      toast.error('Failed to load bonuses');
    } finally {
      setBonusLoading(false);
    }
  }, [filters]);

  // ── Fetch lifetime payout summary (paid only) ──────────────────────────
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const r = await apiClient.get('/finance/incentive-payouts-summary');
      setSummaryData(r.data || { rows: [], totals: {} });
    } catch (err) {
      console.error('payouts-summary error', err);
      toast.error('Failed to load payout summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTab === 'flown') fetchFlown(); }, [fetchFlown, activeTab]);
  useEffect(() => { if (activeTab === 'bonuses') fetchBonuses(); }, [activeTab, fetchBonuses]);
  useEffect(() => { if (activeTab === 'summary') fetchSummary(); }, [activeTab, fetchSummary]);

  const handleMarkPaid = async () => {
    if (!payDialog.row) return;
    setProcessing(true);
    try {
      await apiClient.post(`/finance/flown-incentives/${payDialog.row.portal_id}/mark-paid`, paymentForm);
      toast.success(`Commission paid · also recorded in Expenses (Salaries)`);
      setPayDialog({ open: false, row: null });
      setPaymentForm({ payment_method: 'bank_transfer', payment_reference: '', notes: '' });
      fetchFlown();
      if (activeTab === 'summary') fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };

  const handleReverse = async () => {
    if (!reverseDialog.row) return;
    setProcessing(true);
    try {
      await apiClient.delete(`/finance/flown-incentives/${reverseDialog.row.portal_id}/payment`);
      toast.success('Payment reversed · linked expense removed from P&L');
      setReverseDialog({ open: false, row: null });
      fetchFlown();
      if (activeTab === 'summary') fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse');
    } finally {
      setProcessing(false);
    }
  };

  const handleRecoverOwed = async (row) => {
    if (!window.confirm(
      `Mark ₹${(row.amount || 0).toLocaleString()} owed by ${row.counselor_name} as recovered?\n\n` +
      `This posts a reversing (negative) entry to Expenses → Salaries so the P&L nets out.`
    )) return;
    try {
      await apiClient.post(`/finance/flown-incentives/recover-owed/${row.record_id}`, {});
      toast.success('Recovery recorded · reversing expense posted to P&L');
      fetchFlown();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record recovery');
    }
  };

  // ─── Bonus state-machine actions (Verify → Mark Paid → Reverse) ────────
  const [bonusPayDialog, setBonusPayDialog] = useState({ open: false, row: null });
  const [bonusPaymentForm, setBonusPaymentForm] = useState({
    payment_method: 'bank_transfer', payment_reference: '', notes: '',
    payment_date: new Date().toISOString().slice(0, 10),
  });

  const handleVerifyBonus = async (row) => {
    if (!window.confirm(`Mark this ₹${(row.amount || 0).toLocaleString()} bonus for ${row.counselor_name} as Eligible for payout?`)) return;
    try {
      await apiClient.put(`/counselor-incentive-records/${row.id}/mark-eligible`, {});
      toast.success('Bonus marked Eligible · ready for payout');
      fetchBonuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark eligible');
    }
  };

  const handleRejectBonus = async (row) => {
    // Mandatory reason — open a prompt. Empty / cancelled = abort.
    const reason = window.prompt(
      `Reject ₹${(row.amount || 0).toLocaleString()} bonus for ${row.counselor_name}?\n\nReason (required):`,
      ''
    );
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      await apiClient.put(`/counselor-incentive-records/${row.id}/reject`, { reason: reason.trim() });
      toast.success('Bonus rejected · moved to Rejected filter');
      fetchBonuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleAdjustAmount = async (row) => {
    const input = window.prompt(
      `Adjust bonus amount for ${row.counselor_name}\n(e.g. double/triple-bonus week).\n\nCurrent: ₹${(row.amount || 0).toLocaleString()}\nEnter new amount:`,
      String(row.amount || 0)
    );
    if (input === null) return; // cancelled
    const newAmount = Number(input);
    if (!Number.isFinite(newAmount) || newAmount < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const reason = window.prompt('Reason for the change (required):', '');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    try {
      await apiClient.put(`/counselor-incentive-records/${row.id}/adjust-amount`, { amount: newAmount, reason: reason.trim() });
      toast.success(`Amount updated to ₹${newAmount.toLocaleString()}`);
      fetchBonuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update amount');
    }
  };

  const handleRecomputeTeamBonus = async () => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const defMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
    const month = window.prompt('Recompute team bonuses for which month? (YYYY-MM)\nThis awards ₹ team bonus to each eligible team member.', defMonth);
    if (month === null) return;
    if (!/^\d{4}-\d{2}$/.test(month.trim())) { toast.error('Enter month as YYYY-MM (e.g. 2026-06)'); return; }
    try {
      const res = await apiClient.post('/counselor-incentive-records/recompute-team-bonus', { month: month.trim() });
      const created = res.data?.created || 0;
      if (created > 0) {
        toast.success(`Recorded ${created} team-bonus payout(s) for ${res.data.month}`);
      } else {
        toast.info(`No new team bonuses for ${res.data?.month || month} (no eligible team, or already recorded)`);
      }
      fetchBonuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recompute team bonus');
    }
  };



  const handleRevertBonus = async (row) => {
    if (!window.confirm('Revert this bonus back to Pending so it can be re-evaluated?')) return;
    try {
      await apiClient.put(`/counselor-incentive-records/${row.id}/revert`);
      toast.success('Reverted to Pending');
      fetchBonuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert');
    }
  };

  const handleMarkBonusPaid = async () => {
    if (!bonusPayDialog.row) return;
    setProcessing(true);
    try {
      await apiClient.put(
        `/counselor-incentive-records/${bonusPayDialog.row.id}/mark-paid`,
        bonusPaymentForm,
      );
      toast.success('Bonus paid · Expense recorded (Salaries)');
      setBonusPayDialog({ open: false, row: null });
      setBonusPaymentForm({
        payment_method: 'bank_transfer', payment_reference: '', notes: '',
        payment_date: new Date().toISOString().slice(0, 10),
      });
      fetchBonuses();
      if (activeTab === 'summary') fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark bonus paid');
    } finally {
      setProcessing(false);
    }
  };

  const handleReverseBonus = async (row) => {
    if (!window.confirm(
      `Reverse ₹${(row.amount || 0).toLocaleString()} bonus payment?\n\nThis will soft-delete the linked Expense and return the bonus to Eligible.`,
    )) return;
    try {
      await apiClient.delete(`/counselor-incentive-records/${row.id}/payment`);
      toast.success('Bonus payment reversed · expense removed from P&L');
      fetchBonuses();
      if (activeTab === 'summary') fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse bonus');
    }
  };

  const clearFilters = () => {
    setFilters({
      counselor_id: '',
      paid_status: '',
    });
  };

  // Filter rows for current display (paid_status filter is also handled server-side for Flown,
  // but we keep client filter for the bonuses tab consistency)
  const flownRows = flownData.records || [];
  const flownTotals = flownData.totals || {};

  return (
    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6" data-testid="counselor-incentive-records">
      {/* Header */}
      <div className="sticky top-0 z-10 rounded-xl p-3 sm:p-4" style={{ backgroundColor: COLORS.primary }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance')}
                    className="h-8 text-white/80 hover:text-white hover:bg-white/10" data-testid="incentives-back-btn">
              <i className="fas fa-arrow-left mr-1" /> <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.accent }}>
              <i className="fas fa-trophy text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-white" data-testid="staff-incentives-heading">Staff Incentives</h1>
              <p className="text-[10px] sm:text-xs text-white/70 hidden sm:block">Role incentives & retained bonuses</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canMarkPaid && activeTab === 'bonuses' && (
              <Button size="sm" variant="outline" onClick={handleRecomputeTeamBonus}
                      className="h-7 sm:h-8 text-[9px] sm:text-xs px-2"
                      data-testid="recompute-team-bonus-btn">
                <i className="fas fa-users mr-1" />Recompute Team Bonus
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Headline KPI cards (always reflect Flown Commissions) */}
      {activeTab === 'flown' && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="flown-commission-kpis">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-blue-700 leading-tight">{flownTotals.flown_count || 0}</p>
            <p className="text-[11px] sm:text-xs text-blue-600 mt-0.5">Students Flown</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-yellow-700 leading-tight truncate">₹{(flownTotals.total_amount || 0).toLocaleString()}</p>
            <p className="text-[11px] sm:text-xs text-yellow-600 mt-0.5">Total Commission</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-green-700 leading-tight truncate">₹{(flownTotals.total_paid || 0).toLocaleString()}</p>
            <p className="text-[11px] sm:text-xs text-green-600 mt-0.5">Paid</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3 sm:p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-red-700 leading-tight truncate">₹{(flownTotals.total_pending || 0).toLocaleString()}</p>
            <p className="text-[11px] sm:text-xs text-red-600 mt-0.5">Pending</p>
          </CardContent>
        </Card>
      </div>}

      {/* The basic ledger owns its own management-only filters. */}
      {canFilter && activeTab !== 'basic' && <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-gray-500">Counselor</Label>
              <Select value={filters.counselor_id || 'all'}
                      onValueChange={(v) => setFilters(p => ({ ...p, counselor_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-counselor"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counselors</SelectItem>
                  {counselors.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">Status</Label>
              <Select value={filters.paid_status || 'all'}
                      onValueChange={(v) => setFilters(p => ({ ...p, paid_status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-paid-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="eligible">Eligible</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Flown (Unpaid)</SelectItem>
                  <SelectItem value="clawed_back">Clawed Back / Owed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={clearFilters} className="h-7 text-xs" data-testid="filter-clear-btn">
              <i className="fas fa-times mr-1" /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full mb-3 grid grid-cols-3 h-auto" data-testid="basic-incentive-navigation"><TabsTrigger value="basic" className="min-w-0 min-h-[44px] text-xs sm:text-sm whitespace-normal" data-testid="tab-basic-incentives">Base incentives</TabsTrigger><TabsTrigger value="bonuses" className="min-w-0 min-h-[44px] text-xs sm:text-sm whitespace-normal" data-testid="tab-retained-bonuses">Bonuses</TabsTrigger><TabsTrigger value="summary" className="min-w-0 min-h-[44px] text-xs sm:text-sm whitespace-normal" data-testid="tab-paid-history">Paid history</TabsTrigger></TabsList>
        <TabsContent value="basic"><RoleIncentiveLedger /></TabsContent>
        {false && <TabsList className="grid w-full grid-cols-3 gap-1">
          <TabsTrigger value="flown" data-testid="tab-flown" className="text-[11px] sm:text-sm px-1 whitespace-nowrap">
            <i className="fas fa-plane-departure mr-1 sm:mr-2" />
            <span className="sm:hidden">Flown</span><span className="hidden sm:inline">Flown Commissions</span>
          </TabsTrigger>
          <TabsTrigger value="bonuses" data-testid="tab-bonuses" className="text-[11px] sm:text-sm px-1 whitespace-nowrap">
            <i className="fas fa-gift mr-1 sm:mr-2" />Bonuses
          </TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary" className="text-[11px] sm:text-sm px-1 whitespace-nowrap">
            <i className="fas fa-chart-pie mr-1 sm:mr-2" />Summary
          </TabsTrigger>
        </TabsList>}

        {/* Historical UI retained for archive interpretation, not current payouts. */}
        <TabsContent value="flown">
          <Card className="mb-2">
            <CardContent className="p-3 text-xs text-gray-600">
              <i className="fas fa-info-circle text-blue-500 mr-2" />
              Lists every student currently in the Admission <b>Flown</b> tab. Base commission is read live from
              <b> Settings → Catalog → Programs</b>. Marking paid creates an entry in <b>Expenses → Salaries</b> automatically.
            </CardContent>
          </Card>

          {(flownTotals.clawed_back_count > 0) && (
            <Card className="mb-2 border-red-200 bg-red-50" data-testid="clawback-summary-banner">
              <CardContent className="p-3 text-xs text-red-800 flex flex-wrap items-center justify-between gap-2">
                <span>
                  <i className="fas fa-rotate-left mr-2" />
                  <b>{flownTotals.clawed_back_count}</b> clawed-back commission{flownTotals.clawed_back_count > 1 ? 's' : ''} ·
                  ₹{(flownTotals.total_clawed_back || 0).toLocaleString()} reversed (admission cancelled/deleted)
                </span>
                {flownTotals.total_owed > 0 && (
                  <span className="font-semibold text-red-900" data-testid="clawback-owed-total">
                    <i className="fas fa-triangle-exclamation mr-1" />
                    ₹{(flownTotals.total_owed || 0).toLocaleString()} owed to recover (already paid out)
                  </span>
                )}
              </CardContent>
            </Card>
          )}

          {flownLoading ? (
            <div className="flex items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-2xl text-gray-400" />
            </div>
          ) : flownRows.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <i className={`fas ${filters.paid_status === 'clawed_back' ? 'fa-rotate-left' : 'fa-plane'} text-4xl text-gray-300 mb-4`} />
                <p className="text-gray-500">
                  {filters.paid_status === 'clawed_back'
                    ? 'No clawed-back commissions'
                    : 'No flown students in the selected range'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {filters.paid_status === 'clawed_back'
                    ? 'Commissions appear here when an admission is cancelled or deleted.'
                    : 'Students appear here once admission is marked Flown.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="block sm:hidden space-y-2">
                {flownRows.map((r) => (
                  <Card key={r.record_id || r.portal_id} className={`overflow-hidden ${r.clawed_back ? 'opacity-70 bg-slate-50' : ''}`} data-testid={r.clawed_back ? `clawback-card-${r.record_id || r.portal_id}` : `flown-card-${r.portal_id}`}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-sm">{r.student_name}</p>
                          <p className="text-[11px] text-gray-600">Counselor: <b className="text-gray-800">{r.counselor_name}</b></p>
                        </div>
                        {r.clawed_back ? (
                          <Badge className={`text-[10px] ${r.recovered ? 'bg-green-100 text-green-800 border border-green-300' : r.clawback_owed ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-slate-200 text-slate-700'}`} data-testid={`clawback-badge-${r.record_id || r.portal_id}`}>
                            <i className="fas fa-rotate-left mr-1" />{r.recovered ? 'Recovered' : r.clawback_owed ? 'Owed (recover)' : 'Clawed Back'}
                          </Badge>
                        ) : r.is_paid ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px]">Paid</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 text-[10px]"><i className="fas fa-plane-departure mr-1" />Flown</Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-600 space-y-0.5">
                        <p>Program: <b className="text-indigo-700">{r.program}</b></p>
                        <p>Intake: {r.intake} · Country: {r.country}</p>
                        <p>Flown on: <b>{formatDateIST(r.flown_date)}</b></p>
                        {r.is_paid && <p className="text-green-700">Paid: {formatDateIST(r.paid_at)} by {r.paid_by_name}</p>}
                        {r.clawed_back && (
                          <>
                            <p className="text-red-700">Clawed back: <b>{formatDateIST(r.clawed_back_at)}</b></p>
                            {r.clawback_reason && <p className="text-slate-600 italic">Reason: {r.clawback_reason}</p>}
                          </>
                        )}
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t">
                        <span className={`text-base font-bold text-yellow-700 ${r.clawed_back ? 'line-through text-gray-500' : ''}`}>₹{(r.amount || 0).toLocaleString()}</span>
                        {canMarkPaid && r.clawed_back && r.clawback_owed && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                                  onClick={() => handleRecoverOwed(r)}
                                  data-testid={`recover-owed-btn-${r.record_id || r.portal_id}`}>
                            <i className="fas fa-hand-holding-dollar mr-1" /> Recover
                          </Button>
                        )}
                        {canMarkPaid && !r.clawed_back && (
                          r.is_paid ? (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                    onClick={() => setReverseDialog({ open: true, row: r })}
                                    data-testid={`reverse-btn-${r.portal_id}`}>
                              <i className="fas fa-undo mr-1" /> Reverse
                            </Button>
                          ) : (
                            <Button size="sm" className="h-7 text-[11px] bg-green-600 hover:bg-green-700"
                                    onClick={() => setPayDialog({ open: true, row: r })}
                                    data-testid={`mark-paid-btn-${r.portal_id}`}>
                              <i className="fas fa-check mr-1" /> Mark Paid
                            </Button>
                          )
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block">
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Counselor</TableHead>
                          <TableHead>Program</TableHead>
                          <TableHead>Intake</TableHead>
                          <TableHead>Flown On</TableHead>
                          <TableHead className="text-right">Commission</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Paid On / By</TableHead>
                          {canMarkPaid && <TableHead className="text-right">Action</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {flownRows.map((r) => (
                          <TableRow key={r.record_id || r.portal_id}
                                    data-testid={r.clawed_back ? `clawback-row-${r.record_id || r.portal_id}` : `flown-row-${r.portal_id}`}
                                    className={r.clawed_back ? 'opacity-70 bg-slate-50' : ''}>
                            <TableCell className="font-medium text-sm">{r.student_name}</TableCell>
                            <TableCell className="text-sm">{r.counselor_name}</TableCell>
                            <TableCell className="text-xs text-indigo-700">{r.program}</TableCell>
                            <TableCell className="text-xs text-gray-600">{r.intake}</TableCell>
                            <TableCell className="text-xs">{formatDateIST(r.flown_date)}</TableCell>
                            <TableCell className="text-right font-bold text-yellow-700">
                              <span className={r.clawed_back ? 'line-through text-gray-500' : ''}>₹{(r.amount || 0).toLocaleString()}</span>
                            </TableCell>
                            <TableCell>
                              {r.clawed_back ? (
                                <>
                                  <Badge className={r.recovered ? 'bg-green-100 text-green-800 border border-green-300' : r.clawback_owed ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-slate-200 text-slate-700'} data-testid={`clawback-badge-${r.record_id || r.portal_id}`}>
                                    <i className="fas fa-rotate-left mr-1" />{r.recovered ? 'Recovered' : r.clawback_owed ? 'Owed (recover)' : 'Clawed Back'}
                                  </Badge>
                                  {r.clawback_reason && (
                                    <div className="text-[10px] text-slate-600 mt-1 max-w-[160px] truncate" title={r.clawback_reason}>{r.clawback_reason}</div>
                                  )}
                                </>
                              ) : r.is_paid
                                ? <Badge className="bg-green-100 text-green-800">Paid</Badge>
                                : <Badge className="bg-blue-100 text-blue-800"><i className="fas fa-plane-departure mr-1" />Flown</Badge>}
                            </TableCell>
                            <TableCell className="text-xs text-gray-600">
                              {r.clawed_back
                                ? (r.clawed_back_at ? <div>Clawed {formatDateIST(r.clawed_back_at)}</div> : '—')
                                : r.is_paid ? (<><div>{formatDateIST(r.paid_at)}</div><div className="text-[10px] text-gray-500">{r.paid_by_name}</div></>) : '—'}
                            </TableCell>
                            {canMarkPaid && (
                              <TableCell className="text-right">
                                {r.clawed_back ? (
                                  r.clawback_owed ? (
                                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                            onClick={() => handleRecoverOwed(r)}
                                            data-testid={`recover-owed-btn-${r.record_id || r.portal_id}`}>
                                      <i className="fas fa-hand-holding-dollar mr-1" /> Recover
                                    </Button>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">—</span>
                                  )
                                ) : r.is_paid ? (
                                  <Button size="sm" variant="outline" className="h-7 text-xs"
                                          onClick={() => setReverseDialog({ open: true, row: r })}
                                          data-testid={`reverse-btn-${r.portal_id}`}>
                                    <i className="fas fa-undo mr-1" /> Reverse
                                  </Button>
                                ) : (
                                  <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                          onClick={() => setPayDialog({ open: true, row: r })}
                                          data-testid={`mark-paid-btn-${r.portal_id}`}>
                                    <i className="fas fa-check mr-1" /> Mark Paid
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ─── BONUSES TAB (legacy data) ──────────────────────────────────── */}
        <TabsContent value="bonuses">
          <Card className="mb-2">
            <CardContent className="p-3 text-xs text-gray-600">
              <i className="fas fa-info-circle text-purple-500 mr-2" />
              Bonuses are auto-calculated by the rule engine (Power Hours, Weekly, Monthly, First Strike).
              Base flown commissions live in the <b>Flown Commissions</b> tab.
            </CardContent>
          </Card>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Card className="bg-yellow-50 border-yellow-200"><CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-yellow-700 leading-tight truncate">₹{(bonusTotals.total_amount || 0).toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs text-yellow-600 mt-0.5">Total Bonuses</p>
            </CardContent></Card>
            <Card className="bg-green-50 border-green-200"><CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-green-700 leading-tight truncate">₹{(bonusTotals.total_paid || 0).toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs text-green-600 mt-0.5">Paid</p>
            </CardContent></Card>
            <Card className="bg-red-50 border-red-200"><CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-red-700 leading-tight truncate">₹{(bonusTotals.total_pending || 0).toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs text-red-600 mt-0.5">Pending</p>
            </CardContent></Card>
          </div>

          {bonusLoading ? (
            <div className="flex items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-2xl text-gray-400" />
            </div>
          ) : bonusRecords.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <i className="fas fa-gift text-4xl text-gray-300 mb-4" />
              <p className="text-gray-500">No bonus records in the selected range</p>
            </CardContent></Card>
          ) : (
            <>
            {/* Mobile cards */}
            <div className="block sm:hidden space-y-2" data-testid="bonus-mobile-list">
              {bonusRecords.map(rec => {
                const raw = rec.payment_status || (rec.status === 'paid' ? 'paid' : 'pending');
                const normalized = raw === 'unpaid' ? 'pending' : raw === 'verified' ? 'eligible' : raw;
                const isClawedBack = rec.status === 'clawed_back';
                const isOwed = isClawedBack && rec.clawback_owed === true;
                const isPaid = !isClawedBack && normalized === 'paid';
                const isEligible = !isClawedBack && normalized === 'eligible';
                const isRejected = !isClawedBack && normalized === 'rejected';
                const isPending = !isClawedBack && normalized === 'pending';
                const stateLabel = isOwed ? 'Owed (recover)' : isClawedBack ? 'Clawed Back' : isPaid ? 'Paid' : isEligible ? 'Eligible' : isRejected ? 'Rejected' : 'Pending';
                const stateClass = isOwed ? 'bg-red-100 text-red-800 border border-red-300' : isClawedBack ? 'bg-slate-200 text-slate-700' : isPaid ? 'bg-green-100 text-green-800' : isEligible ? 'bg-blue-100 text-blue-800' : isRejected ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800';
                return (
                  <Card key={rec.id} className={isClawedBack ? 'opacity-70 bg-slate-50' : ''} data-testid={`bonus-card-${rec.id}`}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{rec.counselor_name}</p>
                          <Badge className={`text-[10px] mt-0.5 ${getBonusBadge(rec.incentive_type)}`}>{BONUS_TYPE_LABELS[rec.incentive_type] || rec.incentive_type}</Badge>
                        </div>
                        <Badge className={`text-[10px] shrink-0 ${stateClass}`} data-testid={`bonus-status-${rec.id}`}>{stateLabel}</Badge>
                      </div>
                      <div className="text-[11px] text-gray-600 space-y-0.5">
                        <p>Period: <b className="text-gray-800">{rec.period_label || rec.intake || '-'}</b></p>
                        <p>Created: {formatDateIST(rec.created_at)}</p>
                        {isPaid && <p className="text-green-700">Paid: {formatDateIST(rec.payment_date || rec.paid_at)}</p>}
                        {isRejected && rec.rejection_reason && <p className="text-rose-700 italic truncate">Reason: {rec.rejection_reason}</p>}
                        {isClawedBack && rec.clawback_reason && <p className="text-slate-600 italic truncate">Reason: {rec.clawback_reason}</p>}
                      </div>
                      {canMarkPaid && (isPending || isEligible || isRejected || isPaid) && (
                        <div className="flex justify-between items-center gap-2 mt-2 pt-2 border-t">
                          <span className="text-base font-bold text-yellow-700">₹{(rec.amount || 0).toLocaleString()}</span>
                          <div className="flex flex-wrap justify-end gap-1">
                            {isPending && (<>
                              <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => handleAdjustAmount(rec)} data-testid={`edit-bonus-amount-btn-m-${rec.id}`}><i className="fas fa-pen mr-1" />Edit ₹</Button>
                              <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => handleRejectBonus(rec)} data-testid={`reject-bonus-btn-m-${rec.id}`}><i className="fas fa-times-circle mr-1" />Reject</Button>
                              <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => handleVerifyBonus(rec)} data-testid={`mark-eligible-btn-m-${rec.id}`}><i className="fas fa-check-circle mr-1" />Eligible</Button>
                            </>)}
                            {isEligible && (<>
                              <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => handleAdjustAmount(rec)} data-testid={`edit-bonus-amount-btn-m-${rec.id}`}><i className="fas fa-pen mr-1" />Edit ₹</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={() => handleRevertBonus(rec)} data-testid={`revert-bonus-btn-m-${rec.id}`}><i className="fas fa-undo mr-1" />Revert</Button>
                              <Button size="sm" className="h-7 text-[11px] px-2 bg-green-600 hover:bg-green-700" onClick={() => setBonusPayDialog({ open: true, row: rec })} data-testid={`mark-bonus-paid-btn-m-${rec.id}`}><i className="fas fa-money-bill-wave mr-1" />Mark Paid</Button>
                            </>)}
                            {isRejected && (<Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={() => handleRevertBonus(rec)} data-testid={`unreject-bonus-btn-m-${rec.id}`}><i className="fas fa-undo mr-1" />Un-reject</Button>)}
                            {isPaid && (<Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-red-600 hover:text-red-700" onClick={() => handleReverseBonus(rec)} data-testid={`reverse-bonus-btn-m-${rec.id}`}><i className="fas fa-undo mr-1" />Reverse</Button>)}
                          </div>
                        </div>
                      )}
                      {!(canMarkPaid && (isPending || isEligible || isRejected || isPaid)) && (
                        <div className="mt-2 pt-2 border-t">
                          <span className="text-base font-bold text-yellow-700">₹{(rec.amount || 0).toLocaleString()}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block">
            <Card><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Paid On</TableHead>
                    <TableHead>Created</TableHead>
                    {canMarkPaid && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bonusRecords.map(rec => {
                    // Derive workflow state. Treat legacy `unpaid` / missing
                    // as `pending`, legacy `verified` as `eligible`.
                    const raw = rec.payment_status || (rec.status === 'paid' ? 'paid' : 'pending');
                    const normalized = raw === 'unpaid' ? 'pending' : raw === 'verified' ? 'eligible' : raw;
                    const isClawedBack = rec.status === 'clawed_back';
                    const isOwed = isClawedBack && rec.clawback_owed === true;
                    const isPaid = !isClawedBack && normalized === 'paid';
                    const isEligible = !isClawedBack && normalized === 'eligible';
                    const isRejected = !isClawedBack && normalized === 'rejected';
                    const isPending = !isClawedBack && normalized === 'pending';
                    const stateLabel = isOwed ? 'Owed (recover)'
                      : isClawedBack ? 'Clawed Back'
                      : isPaid ? 'Paid'
                      : isEligible ? 'Eligible'
                      : isRejected ? 'Rejected'
                      : 'Pending';
                    const stateClass = isOwed
                      ? 'bg-red-100 text-red-800 border border-red-300'
                      : isClawedBack
                        ? 'bg-slate-200 text-slate-700'
                        : isPaid
                      ? 'bg-green-100 text-green-800'
                      : isEligible
                        ? 'bg-blue-100 text-blue-800'
                        : isRejected
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-amber-100 text-amber-800';
                    return (
                      <TableRow key={rec.id} data-testid={`bonus-row-${rec.id}`}>
                        <TableCell className="font-medium">{rec.counselor_name}</TableCell>
                        <TableCell><Badge className={`text-xs ${getBonusBadge(rec.incentive_type)}`}>{BONUS_TYPE_LABELS[rec.incentive_type] || rec.incentive_type}</Badge></TableCell>
                        <TableCell className="text-xs text-gray-600">{rec.period_label || rec.intake || '-'}</TableCell>
                        <TableCell className="text-right font-bold text-yellow-700">₹{(rec.amount || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={stateClass} data-testid={`bonus-status-${rec.id}`}>{stateLabel}</Badge>
                          {isClawedBack && rec.clawback_reason && (
                            <div className="text-[10px] text-slate-600 mt-1 max-w-[180px] truncate" title={rec.clawback_reason}>
                              {rec.clawback_reason}
                            </div>
                          )}
                          {isRejected && rec.rejection_reason && (
                            <div className="text-[10px] text-rose-700 mt-1 max-w-[160px] truncate" title={rec.rejection_reason}>
                              {rec.rejection_reason}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{isPaid ? formatDateIST(rec.payment_date || rec.paid_at) : '-'}</TableCell>
                        <TableCell className="text-xs">{formatDateIST(rec.created_at)}</TableCell>
                        {canMarkPaid && (
                          <TableCell className="text-right whitespace-nowrap">
                            {/* PENDING: Edit amount + Reject + Mark Eligible */}
                            {isPending && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs mr-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                                  onClick={() => handleAdjustAmount(rec)}
                                  data-testid={`edit-bonus-amount-btn-${rec.id}`}>
                                  <i className="fas fa-pen mr-1" />Edit ₹
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs mr-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                                  onClick={() => handleRejectBonus(rec)}
                                  data-testid={`reject-bonus-btn-${rec.id}`}>
                                  <i className="fas fa-times-circle mr-1" />Reject
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs mr-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleVerifyBonus(rec)}
                                  data-testid={`mark-eligible-btn-${rec.id}`}>
                                  <i className="fas fa-check-circle mr-1" />Mark Eligible
                                </Button>
                              </>
                            )}
                            {/* ELIGIBLE: Edit amount + Revert + Mark Paid */}
                            {isEligible && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs mr-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                                  onClick={() => handleAdjustAmount(rec)}
                                  data-testid={`edit-bonus-amount-btn-${rec.id}`}>
                                  <i className="fas fa-pen mr-1" />Edit ₹
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs mr-1"
                                  onClick={() => handleRevertBonus(rec)}
                                  data-testid={`revert-bonus-btn-${rec.id}`}>
                                  <i className="fas fa-undo mr-1" />Revert
                                </Button>
                                <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                  onClick={() => setBonusPayDialog({ open: true, row: rec })}
                                  data-testid={`mark-bonus-paid-btn-${rec.id}`}>
                                  <i className="fas fa-money-bill-wave mr-1" />Mark Paid
                                </Button>
                              </>
                            )}
                            {/* REJECTED: only Revert (un-reject) */}
                            {isRejected && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs"
                                onClick={() => handleRevertBonus(rec)}
                                data-testid={`unreject-bonus-btn-${rec.id}`}>
                                <i className="fas fa-undo mr-1" />Un-reject
                              </Button>
                            )}
                            {/* PAID: Reverse */}
                            {isPaid && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:text-red-700"
                                onClick={() => handleReverseBonus(rec)}
                                data-testid={`reverse-bonus-btn-${rec.id}`}>
                                <i className="fas fa-undo mr-1" />Reverse
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent></Card>
            </div>
            </>
          )}
        </TabsContent>

        {/* ─── SUMMARY TAB — Lifetime PAID payouts (commission + bonus) ──── */}
        <TabsContent value="summary">
          <Card className="mb-3 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-3 sm:p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <p className="text-[11px] sm:text-xs text-emerald-700/80 font-medium">Total Payout (Lifetime)</p>
                  <p className="text-lg sm:text-2xl font-bold text-emerald-800">
                    ₹{(summaryData.totals?.grand_total || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] sm:text-xs text-yellow-700/80 font-medium">Commission Paid</p>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-800">
                    ₹{(summaryData.totals?.grand_commission || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] sm:text-xs text-purple-700/80 font-medium">Bonus Paid</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-800">
                    ₹{(summaryData.totals?.grand_bonus || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] sm:text-xs text-indigo-700/80 font-medium">Counselors Paid</p>
                  <p className="text-lg sm:text-2xl font-bold text-indigo-800">
                    {summaryData.totals?.counselor_count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <i className="fas fa-trophy text-amber-500" /> Total Incentive Paid by Counselor
              </CardTitle>
              <CardDescription className="text-xs">
                Lifetime PAID payouts only (commission + bonus). Pending amounts are excluded.
                Includes deactivated counselors so the company sees full historical liability.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {summaryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="fas fa-spinner fa-spin text-2xl text-gray-400" />
                </div>
              ) : (summaryData.rows || []).length === 0 ? (
                <div className="p-12 text-center">
                  <i className="fas fa-coins text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">No incentive payouts recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Mark a flown commission or bonus as paid to start tracking lifetime payouts.</p>
                </div>
              ) : (
                <>
                  {/* Mobile cards */}
                  <div className="block sm:hidden p-2 space-y-2">
                    {(summaryData.rows || []).map((r) => (
                      <Card key={r.counselor_id || r.counselor_name} className="border" data-testid={`summary-card-${r.counselor_id || 'unassigned'}`}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm">{r.counselor_name}</p>
                              {r.is_active === false && (
                                <Badge className="bg-gray-200 text-gray-700 text-[9px] px-1.5">Deactivated</Badge>
                              )}
                            </div>
                            <p className="text-base font-bold text-emerald-700">₹{(r.total_paid || 0).toLocaleString()}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-[11px]">
                            <div className="bg-yellow-50 rounded p-1.5">
                              <p className="text-yellow-700/70">Commission</p>
                              <p className="font-semibold text-yellow-800">
                                ₹{(r.commission_paid || 0).toLocaleString()} <span className="text-[9px] opacity-70">({r.commission_count})</span>
                              </p>
                            </div>
                            <div className="bg-purple-50 rounded p-1.5">
                              <p className="text-purple-700/70">Bonus</p>
                              <p className="font-semibold text-purple-800">
                                ₹{(r.bonus_paid || 0).toLocaleString()} <span className="text-[9px] opacity-70">({r.bonus_count})</span>
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Counselor</TableHead>
                          <TableHead className="text-right">Commission Paid</TableHead>
                          <TableHead className="text-right">Bonus Paid</TableHead>
                          <TableHead className="text-right">Total Paid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(summaryData.rows || []).map((r) => (
                          <TableRow key={r.counselor_id || r.counselor_name} data-testid={`summary-row-${r.counselor_id || 'unassigned'}`}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <span>{r.counselor_name}</span>
                                {r.is_active === false && (
                                  <Badge className="bg-gray-200 text-gray-700 text-[10px]" title="Counselor deactivated">Deactivated</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-yellow-700">
                              ₹{(r.commission_paid || 0).toLocaleString()}
                              <span className="text-[10px] text-gray-500 ml-1">({r.commission_count})</span>
                            </TableCell>
                            <TableCell className="text-right text-purple-700">
                              ₹{(r.bonus_paid || 0).toLocaleString()}
                              <span className="text-[10px] text-gray-500 ml-1">({r.bonus_count})</span>
                            </TableCell>
                            <TableCell className="text-right font-bold text-emerald-700">
                              ₹{(r.total_paid || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Mark Paid Dialog ─────────────────────────────────────────────── */}
      <Dialog open={payDialog.open} onOpenChange={(o) => !o && setPayDialog({ open: false, row: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Commission Paid</DialogTitle>
            <DialogDescription className="text-xs">
              This will record an Expense (category: Salaries) for the same amount in the company P&L.
            </DialogDescription>
          </DialogHeader>
          {payDialog.row && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{payDialog.row.counselor_name}</p>
                <p className="text-xs text-gray-600">For: {payDialog.row.student_name} ({payDialog.row.program})</p>
                <p className="text-xl font-bold text-yellow-700 mt-2">₹{(payDialog.row.amount || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Payment Method</Label>
                  <Select value={paymentForm.payment_method}
                          onValueChange={(v) => setPaymentForm(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Reference Number</Label>
                  <Input placeholder="UTR / Cheque No." value={paymentForm.payment_reference}
                         onChange={(e) => setPaymentForm(p => ({ ...p, payment_reference: e.target.value }))}
                         data-testid="pay-reference" />
                </div>
                <div>
                  <Label className="text-sm">Notes (Optional)</Label>
                  <Input placeholder="Any notes" value={paymentForm.notes}
                         onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                         data-testid="pay-notes" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={processing} className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-pay-btn">
              {processing ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-check mr-2" />}
              Confirm & Record Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Reverse Paid Dialog ───────────────────────────────────────────── */}
      <Dialog open={reverseDialog.open} onOpenChange={(o) => !o && setReverseDialog({ open: false, row: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse Payment?</DialogTitle>
            <DialogDescription className="text-xs">
              This will remove the linked Expense entry from the P&L. The student will move back to Pending.
            </DialogDescription>
          </DialogHeader>
          {reverseDialog.row && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <b>{reverseDialog.row.counselor_name}</b> · ₹{(reverseDialog.row.amount || 0).toLocaleString()} for{' '}
              <b>{reverseDialog.row.student_name}</b>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={handleReverse} disabled={processing} variant="destructive" data-testid="confirm-reverse-btn">
              {processing ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-undo mr-2" />}
              Reverse Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Bonus Mark Paid Dialog ───────────────────────────────────────── */}
      <Dialog open={bonusPayDialog.open} onOpenChange={(o) => !o && setBonusPayDialog({ open: false, row: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Bonus Paid</DialogTitle>
            <DialogDescription className="text-xs">
              This will record an Expense (category: Salaries · counselor_bonus) in the company P&L.
            </DialogDescription>
          </DialogHeader>
          {bonusPayDialog.row && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{bonusPayDialog.row.counselor_name}</p>
                <p className="text-xs text-gray-600">
                  {BONUS_TYPE_LABELS[bonusPayDialog.row.incentive_type] || bonusPayDialog.row.incentive_type}
                  {bonusPayDialog.row.period_label ? ` · ${bonusPayDialog.row.period_label}` : ''}
                </p>
                <p className="text-xl font-bold text-yellow-700 mt-2">
                  ₹{(bonusPayDialog.row.amount || 0).toLocaleString()}
                </p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Payment Date</Label>
                  <Input type="date" value={bonusPaymentForm.payment_date}
                         onChange={(e) => setBonusPaymentForm(p => ({ ...p, payment_date: e.target.value }))}
                         data-testid="bonus-pay-date" />
                </div>
                <div>
                  <Label className="text-sm">Payment Mode</Label>
                  <Select value={bonusPaymentForm.payment_method}
                          onValueChange={(v) => setBonusPaymentForm(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger data-testid="bonus-pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Reference Number</Label>
                  <Input placeholder="UTR / Cheque No." value={bonusPaymentForm.payment_reference}
                         onChange={(e) => setBonusPaymentForm(p => ({ ...p, payment_reference: e.target.value }))}
                         data-testid="bonus-pay-reference" />
                </div>
                <div>
                  <Label className="text-sm">Notes (Optional)</Label>
                  <Input placeholder="Any notes" value={bonusPaymentForm.notes}
                         onChange={(e) => setBonusPaymentForm(p => ({ ...p, notes: e.target.value }))}
                         data-testid="bonus-pay-notes" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBonusPayDialog({ open: false, row: null })}>Cancel</Button>
            <Button onClick={handleMarkBonusPaid} disabled={processing}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-bonus-pay-btn">
              {processing ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-check mr-2" />}
              Confirm & Record Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
